import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";

export function Login() {
  const { login } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "client" ? "/portal" : "/admin");
    } catch (e) {
      toast.error("Invalid email or password");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="login-page" className="fade-in py-20">
      <div className="container-gos max-w-md">
        <div className="mb-8"><BrandMark size="lg" testid="login-brand" /></div>
        <div className="overline mb-3">Sign in</div>
        <h1 className="font-display text-4xl text-gos-charcoal tracking-tight">Welcome back.</h1>
        <p className="text-sm text-gos-slate mt-2">Access your {settings.brand_name} account.</p>
        <form onSubmit={submit} className="mt-8 bg-white border border-gos-border rounded-sm p-6 space-y-4">
          <Field label="Email" type="email" value={email} onChange={setEmail} required testid="login-email" />
          <Field label="Password" type="password" value={password} onChange={setPassword} required testid="login-password" />
          <button type="submit" disabled={submitting} data-testid="login-submit-btn" className="btn-primary w-full">{submitting ? "…" : "Sign in"}</button>
          <p className="text-sm text-gos-slate text-center">New to {settings.brand_name}? <Link to="/register" className="text-gos-navy hover:text-gos-sand underline">Create account</Link></p>
        </form>
        <div className="mt-6 text-xs text-gos-muted bg-gos-subtle p-4 rounded-sm">
          <div className="font-medium mb-1">Demo credentials</div>
          <div>Admin: admin@globaloffice.com / Admin@2026</div>
          <div>Client: client@demo.com / Client@2026</div>
        </div>
      </div>
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", company: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await register(form);
      toast.success("Account created");
      navigate("/portal");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally { setSubmitting(false); }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="register-page" className="fade-in py-20">
      <div className="container-gos max-w-md">
        <div className="overline mb-3">Create account</div>
        <h1 className="font-display text-4xl text-gos-charcoal tracking-tight">Start your business journey.</h1>
        <form onSubmit={submit} className="mt-8 bg-white border border-gos-border rounded-sm p-6 space-y-4">
          <Field label="Full name" value={form.full_name} onChange={(v) => upd("full_name", v)} required testid="reg-name" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => upd("email", v)} required testid="reg-email" />
          <Field label="Password" type="password" value={form.password} onChange={(v) => upd("password", v)} required testid="reg-password" />
          <Field label="Company (optional)" value={form.company} onChange={(v) => upd("company", v)} testid="reg-company" />
          <Field label="Phone (optional)" value={form.phone} onChange={(v) => upd("phone", v)} testid="reg-phone" />
          <button type="submit" disabled={submitting} data-testid="reg-submit-btn" className="btn-primary w-full">{submitting ? "…" : "Create account"}</button>
          <p className="text-sm text-gos-slate text-center">Already have an account? <Link to="/login" className="text-gos-navy hover:text-gos-sand underline">Sign in</Link></p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">{label}{required && " *"}</label>
      <input data-testid={testid} type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy" />
    </div>
  );
}
